import { Suspense } from 'react';
import { Navigate, useParams } from 'react-router';
import { UsageDetail } from '@gonfalon/billing/components';
import { useVisibleUsageTabs } from '@gonfalon/billing/hooks';
import { AggregationType, UsageTab, type UsageTabKey } from '@gonfalon/billing/types';

import { Card, ErrorCallout } from '../../shared';
import { Box, Flex } from '../../shared/components';
import { useSettings } from '../../shared/hooks/useSettings';
import { useAccount } from '../hooks/useAccount';
import { useAccountSubscription } from '../hooks/useAccountSubscription';

import { AccountContextProvider } from './AccountContextProvider';
import { AccountHeading } from './AccountHeading';

import './PlanUsageDetailsPage.scss';

function DataExportDetail({ accountId, usageTab }: { accountId: string; usageTab: UsageTabKey }) {
  const { tabs, isPending } = useVisibleUsageTabs();
  const isCombinedVisible = tabs.includes(UsageTab.DATA_EXPORT_COMBINED);

  if (usageTab === UsageTab.DATA_EXPORT && isCombinedVisible) {
    return <Navigate to={`/accounts/${accountId}/plan-usage/details/${UsageTab.DATA_EXPORT_COMBINED}`} replace />;
  }
  if (isPending) {
    return null;
  }
  if (usageTab === UsageTab.DATA_EXPORT_COMBINED && !isCombinedVisible) {
    return <Navigate to={`/accounts/${accountId}/plan-usage/details/${UsageTab.DATA_EXPORT}`} replace />;
  }
  return <UsageDetail usageTab={usageTab} defaultAggregationType={AggregationType.MONTH_TO_DATE} />;
}

function PlanUsageDetail({ accountId, usageTab }: { accountId: string; usageTab: UsageTabKey }) {
  if (usageTab === UsageTab.DATA_EXPORT || usageTab === UsageTab.DATA_EXPORT_COMBINED) {
    return <DataExportDetail accountId={accountId} usageTab={usageTab} />;
  }
  return <UsageDetail usageTab={usageTab} defaultAggregationType={AggregationType.MONTH_TO_DATE} />;
}

export function PlanUsageDetailsPage() {
  const { accountId, metricType } = useParams<{ accountId: string; metricType: string }>();

  // Fetch account data. Hooks must run unconditionally, so these run before
  // the accountId/metricType guard below; useAccount disables its query
  // when accountId is empty, and useAccountSubscription already accepts
  // null/undefined.
  const accountQuery = useAccount(accountId ?? '');
  const account = accountId ? accountQuery.data?.entities?.accounts?.[accountId] : undefined;
  const { data: subscription, isLoading, error } = useAccountSubscription(account || null);
  const { vitalstatistixDataExportObservability } = useSettings();

  if (!accountId || !metricType) {
    return (
      <div>
        <ErrorCallout>
          <strong>Invalid URL</strong>
          <p>Missing account ID or metric type in the URL.</p>
        </ErrorCallout>
      </div>
    );
  }

  if (accountQuery.isLoading) {
    return (
      <div className="PlanUsageDetailsPage">
        <Flex>
          <Box width={1} p={2}>
            <Card>
              <div>Loading account data...</div>
            </Card>
          </Box>
        </Flex>
      </div>
    );
  }

  if (accountQuery.error || !account) {
    return (
      <div className="PlanUsageDetailsPage">
        <Flex>
          <Box width={1} p={2}>
            <Card>
              <ErrorCallout>
                <strong>Failed to load account data</strong>
                <p>Account not found or could not be loaded.</p>
              </ErrorCallout>
            </Card>
          </Box>
        </Flex>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="PlanUsageDetailsPage">
        <Flex>
          <Box width={1} p={2}>
            <Card>
              <div>Loading plan usage details...</div>
            </Card>
          </Box>
        </Flex>
      </div>
    );
  }

  if (error) {
    return (
      <div className="PlanUsageDetailsPage">
        <Flex>
          <Box width={1} p={2}>
            <Card>
              <ErrorCallout>
                <strong>Failed to load plan usage data</strong>
                <p>Plan usage data could not be loaded. Please try refreshing the page.</p>
              </ErrorCallout>
            </Card>
          </Box>
        </Flex>
      </div>
    );
  }

  return (
    <div className="PlanUsageDetailsPage">
      <AccountHeading account={account} />
      <Flex>
        <Box width={1} p={2}>
          <Card>
            <Suspense fallback={<div>Loading usage details...</div>}>
              <AccountContextProvider
                key={String(vitalstatistixDataExportObservability)}
                account={account}
                subscription={subscription || undefined}
              >
                <PlanUsageDetail accountId={accountId} usageTab={metricType as UsageTabKey} />
              </AccountContextProvider>
            </Suspense>
          </Card>
        </Box>
      </Flex>
    </div>
  );
}
